import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../../../api/axios";
import { sanitizeReturnPath } from "../../../utils/authReturnUrl";
import {
  clampModulePosition,
  loadPdfBlobUrl,
  normalizeModules,
  resolveDrawingPdfUrl,
} from "./drawingPdfUtils";
import {
  DEFAULT_MEASURE_SCALE,
  dist,
  isModuleHitTarget,
  normalizePenPath,
} from "./drawingAnnotationUtils";
import {
  loadProjectChecklists,
  loadProjectDeviations,
} from "./drawingViewerRelatedData";
import { attachDeviationDrawingCapture, createDeviation } from "../../../api/quality/deviation";
import { createChecklist } from "../../../api/quality/checklist";
import {
  createDrawingIfcAlignment,
  openDrawingIn3d,
} from "../../../api/ifcViewer";
import { useCompanyHasModule, useUserHasAccess } from "../../../routes/roleOnly";
import { uploadDocumentFiles } from "../Media/document-upload-service";
import {
  CHECKLIST_PIN_COLOR,
  checklistIdFromModule,
  documentIdFromModule,
  isLockedObservationPin,
  moduleVisibleOnPage,
  observationKindFromModule,
  OBSERVATION_PIN,
  parseModuleMeta,
  stringifyModuleMeta,
} from "./drawingObservationUtils";
import { captureDrawingRegionSnapshot } from "./drawingSnapshotCapture";
import {
  loadPendingPins,
  makeLocalPinId,
  savePendingPins,
} from "./drawingPendingPins";
import {
  cancelDocumentPlacementSession,
  completeDocumentPlacementSession,
  getDocumentPlacementSession,
  updateDocumentPlacementSession,
} from "./drawingDocumentPlacementSession";
import {
  clamp,
  DEFAULT_DEVIATION_MARKER_TYPE,
  DEFAULT_MODULE_SIZE,
  deviationMarkerPlacement,
  isViewerChromeTarget,
  MARKUP_COLORS,
  SHAPE_TOOLS,
  TOOL_KEYMAP,
} from "./drawingViewerConstants";
import { DEFAULT_DRAWING_KIND } from "./drawingKinds";

// Tools that never mutate the drawing — always allowed, even for view-only users.
const VIEW_ONLY_TOOLS = new Set(["select", "pan"]);

export const useDrawingViewer = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  // Drawings edit rights gate every mutating action in the viewer (save, versions,
  // markup, module edits, editor navigation). View-only users get a read-only viewer.
  const canEdit = useUserHasAccess({ role: "drawings_edit" });
  // Wrap a mutating handler so it no-ops for view-only users (defense-in-depth behind
  // the hidden UI; the API also enforces drawings_edit).
  const guardEdit = (fn) => (...args) =>
    !canEdit ? undefined : typeof fn === "function" ? fn(...args) : undefined;
  const setEditTool = (next) => {
    if (!canEdit && !VIEW_ONLY_TOOLS.has(next)) return;
    setTool(next);
  };
  const containerRef = useRef(null);
  // Tracks canvas mount so wheel/pinch listeners re-bind after loading shell unmounts.
  const [containerEl, setContainerEl] = useState(null);
  const sheetRef = useRef(null);
  const gestureRef = useRef(null);
  // When retrying a failed optimistic pin, carry its local id to reuse on success
  const retryExistingLocalIdRef = useRef(null);
  const zoomRef = useRef(0.7);
  const panRef = useRef({ x: 80, y: 60 });
  const autoFitEnabledRef = useRef(true);
  const pinchRef = useRef(null);
  const pinchActiveRef = useRef(false);
  // When navigating to a different page to focus a module, queue the center
  // until the requested page is active and has rendered.
  const pendingCenterRef = useRef(null);

  const setContainerRef = useCallback((node) => {
    containerRef.current = node;
    setContainerEl(node);
  }, []);

  const [drawing, setDrawing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [zoom, setZoom] = useState(0.7);
  const [pan, setPan] = useState({ x: 80, y: 60 });
  const [pdfBaseSize, setPdfBaseSize] = useState({ width: 0, height: 0 });
  const [numPages, setNumPages] = useState(1);
  const [page, setPage] = useState(0);

  const [modules, setModules] = useState([]);
  const [tool, setTool] = useState("select");
  const [markupColor, setMarkupColor] = useState(MARKUP_COLORS[4].val);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleConfigDialog, setModuleConfigDialog] = useState(null);
  const [draftRect, setDraftRect] = useState(null);
  const [draftPen, setDraftPen] = useState(null);
  const [draftMeasure, setDraftMeasure] = useState(null);
  const [strokeWidth, setStrokeWidth] = useState(2.5);
  const [saving, setSaving] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [panelOpen, setPanelOpen] = useState(true);
  const [panelTab, setPanelTab] = useState("modules");
  const [spaceDown, setSpaceDown] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [measureScale, setMeasureScale] = useState(DEFAULT_MEASURE_SCALE);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [pinDeviationId, setPinDeviationId] = useState(null);
  const [calibrateOpen, setCalibrateOpen] = useState(false);
  const [calibrateLine, setCalibrateLine] = useState(null);
  const [expandedChecklistId, setExpandedChecklistId] = useState(null);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [draftDrawingFloorId, setDraftDrawingFloorId] = useState(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogKind, setAddDialogKind] = useState("photo");
  const [addDialogChecklistId, setAddDialogChecklistId] = useState(null);
  const [addDialogChecklistMode, setAddDialogChecklistMode] = useState(null);
  const [pendingObservation, setPendingObservation] = useState(null);
  const pendingObservationRef = useRef(null);
  const [placementGhost, setPlacementGhost] = useState(null);
  const [sketchSession, setSketchSession] = useState(null);
  const [relocatingModuleId, setRelocatingModuleId] = useState(null);
  const documentPinSessionStartedRef = useRef(null);
  const [moduleDetailModule, setModuleDetailModule] = useState(null);
  const [siblingDrawings, setSiblingDrawings] = useState([]);
  const [siblingDrawingsLoading, setSiblingDrawingsLoading] = useState(false);
  const pendingPinsRef = useRef([]);
  // Ensure we only auto-resume pending pin sync once per drawing/version load.
  const autoResumeKeyRef = useRef(null);
  // Preserve unsaved per-version relocations (pendingChanges) across version switches.
  const pendingChangesByVersionRef = useRef({});
  const activeVersionIdRef = useRef(null);
  const pendingChangesRef = useRef({});
  const [ifcContextMenu, setIfcContextMenu] = useState(null);
  const [ifcAlignmentActive, setIfcAlignmentActive] = useState(false);
  const [ifcAlignmentPoints, setIfcAlignmentPoints] = useState([]);
  const [ifcAlignmentDialogOpen, setIfcAlignmentDialogOpen] = useState(false);
  const [savingIfcAlignment, setSavingIfcAlignment] = useState(false);

  const isVersionMode = activeVersionId != null;
  const activeVersion = versions.find((version) => version.id === activeVersionId);

  const pendingCount = Object.keys(pendingChanges).length;
  const isShapeTool = SHAPE_TOOLS.includes(tool);
  const visibleModules = modules.filter((module) => moduleVisibleOnPage(module, page));
  const normalizeDrawingKind = useCallback((kind) => {
    const normalized = typeof kind === "string" ? kind.trim() : kind;
    return normalized || DEFAULT_DRAWING_KIND;
  }, []);

  const toSheet = useCallback(
    (clientX, clientY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: (clientX - rect.left - pan.x) / zoom,
        y: (clientY - rect.top - pan.y) / zoom,
      };
    },
    [pan, zoom]
  );

  const markViewUserAdjusted = useCallback(() => {
    autoFitEnabledRef.current = false;
  }, []);

  const applyZoom = useCallback((nextZoom) => {
    const clamped = clamp(nextZoom, 0.15, 6);
    zoomRef.current = clamped;
    setZoom(clamped);
    return clamped;
  }, []);

  const applyPan = useCallback((nextPan) => {
    panRef.current = nextPan;
    setPan(nextPan);
  }, []);

  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el || pdfBaseSize.width <= 0 || pdfBaseSize.height <= 0) return;

    const rect = el.getBoundingClientRect();
    if (rect.width < 50 || rect.height < 50) {
      requestAnimationFrame(fit);
      return;
    }

    const pad = 70;
    const nextZoom = clamp(
      Math.min(
        (rect.width - pad * 2) / pdfBaseSize.width,
        (rect.height - pad * 2) / pdfBaseSize.height
      ),
      0.15,
      6
    );

    autoFitEnabledRef.current = true;
    applyZoom(nextZoom);
    applyPan({
      x: (rect.width - pdfBaseSize.width * nextZoom) / 2,
      y: (rect.height - pdfBaseSize.height * nextZoom) / 2,
    });
  }, [applyPan, applyZoom, pdfBaseSize]);

  const zoomAbout = useCallback(
    (nextZoom, cx, cy) => {
      const currentZoom = zoomRef.current || 1;
      const clamped = clamp(nextZoom, 0.15, 6);
      const currentPan = panRef.current;
      markViewUserAdjusted();
      applyZoom(clamped);
      applyPan({
        x: cx - ((cx - currentPan.x) / currentZoom) * clamped,
        y: cy - ((cy - currentPan.y) / currentZoom) * clamped,
      });
    },
    [applyPan, applyZoom, markViewUserAdjusted]
  );

  const setZoomCentered = useCallback(
    (nextZoom) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      zoomAbout(nextZoom, rect.width / 2, rect.height / 2);
    },
    [zoomAbout]
  );

  const centerOn = useCallback(
    (x, y) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const currentZoom = zoomRef.current;
      applyPan({
        x: rect.width / 2 - x * currentZoom,
        y: rect.height * 0.46 - y * currentZoom,
      });
    },
    [applyPan]
  );

  const fetchSiblingDrawings = useCallback(async (projectId, options = {}) => {
    const { drawingFloorId, shouldStop } = options;
    const perPage = 100;
    const fields =
      "id,title,project,floor,house,drawing_kind,drawing_floor_id,drawing_floor,updated_at";
    const allRows = [];
    const seenIds = new Set();
    let pageNumber = 1;
    const shouldPaginateFully = Boolean(projectId || drawingFloorId);

    for (;;) {
      const params = {
        per_page: perPage,
        page: pageNumber,
        // Include floor/house so the drawing switcher can show etasje + hus labels.
        fields,
      };
      if (projectId) {
        params.project_id = projectId;
      }
      if (drawingFloorId) {
        params.drawing_floor_id = drawingFloorId;
      }
      if (shouldStop?.()) break;

      const response = await axios({
        method: "get",
        url: "/drawings/",
        params,
      });

      const pageRows = Array.isArray(response.data?.data) ? response.data.data : [];
      let addedCount = 0;
      pageRows.forEach((row) => {
        const rowId = row?.id == null ? null : String(row.id);
        if (rowId && seenIds.has(rowId)) return;
        if (rowId) seenIds.add(rowId);
        allRows.push(row);
        addedCount += 1;
      });

      // Continue while pages are full and still adding unseen rows.
      if (pageRows.length < perPage || addedCount === 0) {
        break;
      }
      // Without project/floor scope this can become account-wide scanning.
      if (!shouldPaginateFully) {
        break;
      }
      if (shouldStop?.()) break;
      pageNumber += 1;
    }

    return allRows;
  }, []);

  const loadDrawing = useCallback(async (versionId = null) => {
    try {
      setLoading(true);
      setPdfError(null);

      // Guard against /drawings/undefined after failed creates navigating without an id.
      if (id == null || id === "" || id === "undefined" || id === "null") {
        toast.error(t("drawings.load_error"));
        setLoading(false);
        navigate("/dashboard/drawings");
        return;
      }

      const response = await axios({
        method: "get",
        url: `/drawings/${id}`,
        params: {
          version_id: versionId || "live",
        },
      });
      const drawingData = response.data;
      setDrawing(drawingData);
      {
        const serverModules = normalizeModules(drawingData.drawing_modules || []);
        const stored = loadPendingPins(drawingData.id, versionId || null);
        pendingPinsRef.current = stored;
        const localModules = (stored || [])
          .filter((pin) => pin.syncStatus === "pending" || pin.syncStatus === "error")
          .map((pin) => {
            const meta = parseModuleMeta({ path_data: pin.path_data });
            const markerType =
              pin.module_type ||
              meta.marker_type ||
              (pin.kind === "deviation" ? "circle" : pin.kind);
            return {
              id: pin.id,
              module_type: markerType,
              x_position: pin.x,
              y_position: pin.y,
              width: pin.width,
              height: pin.height,
              color: pin.color,
              path_data: pin.path_data,
              deviation_id: pin.payload?.deviation_id || null,
              _localPending: true,
              _syncStatus: pin.syncStatus,
              _pendingPayload: pin.payload,
            };
          });
        setModules([...serverModules, ...localModules]);
      }
      setDraftDrawingFloorId(drawingData.drawing_floor?.id || null);
      setMeasureScale(
        parseFloat(drawingData.measure_scale_mm_per_unit) || DEFAULT_MEASURE_SCALE
      );
      if (Array.isArray(drawingData.drawing_versions)) {
        setVersions(drawingData.drawing_versions);
      }

      const pdfFileUrl = await resolveDrawingPdfUrl(drawingData);
      if (pdfFileUrl) {
        try {
          const url = await loadPdfBlobUrl(pdfFileUrl);
          setPdfUrl(url);
        } catch (err) {
          setPdfError(err.message || t("drawings.viewer.pdf_error"));
          toast.error(t("drawings.load_error"));
        }
      } else {
        setPdfError(t("drawings.viewer.pdf_error"));
        toast.error(t("drawings.load_error"));
      }
    } catch (err) {
      console.error("Error loading drawing:", err);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  }, [id, t, navigate]);

  const switchVersion = useCallback(
    async (versionId) => {
      // Persist current version's unsaved relocations before switching.
      const prevKey = activeVersionIdRef.current || "live";
      pendingChangesByVersionRef.current[prevKey] = pendingChangesRef.current || {};

      setActiveVersionId(versionId);
      setSelectedModule(null);
      try {
        setLoading(true);
        const response = await axios.get(`/drawings/${id}`, {
          params: { version_id: versionId || "live" },
        });
        const serverModules = normalizeModules(response.data.drawing_modules || []);
        // Load any unsaved relocations for the target version and reapply to canvas modules.
        const nextKey = versionId || "live";
        const nextPending = pendingChangesByVersionRef.current[nextKey] || {};
        // Load locally pending pins for the target version only.
        const stored = loadPendingPins(drawing?.id, versionId || null) || [];
        pendingPinsRef.current = stored;
        const localModules = (stored || [])
          .filter((pin) => pin.syncStatus === "pending" || pin.syncStatus === "error")
          .map((pin) => {
            const meta = parseModuleMeta({ path_data: pin.path_data });
            const markerType =
              pin.module_type ||
              meta.marker_type ||
              (pin.kind === "deviation" ? "circle" : pin.kind);
            return {
              id: pin.id,
              module_type: markerType,
              x_position: pin.x,
              y_position: pin.y,
              width: pin.width,
              height: pin.height,
              color: pin.color,
              path_data: pin.path_data,
              deviation_id: pin.payload?.deviation_id || null,
              _localPending: true,
              _syncStatus: pin.syncStatus,
              _pendingPayload: pin.payload,
            };
          });
        const withLocal = [...serverModules, ...localModules];
        const rehydrated = withLocal.map((m) => {
          const patch = nextPending[m.id];
          return patch
            ? {
                ...m,
                x_position:
                  typeof patch.x_position === "number" ? patch.x_position : m.x_position,
                y_position:
                  typeof patch.y_position === "number" ? patch.y_position : m.y_position,
              }
            : m;
        });
        setModules(rehydrated);
        setPendingChanges(nextPending);
      } catch (err) {
        console.error("Error switching drawing version:", err);
        toast.error(t("drawings.load_error"));
      } finally {
        setLoading(false);
      }
    },
    [id, t]
  );

  const handleCreateVersion = async () => {
    const title = window.prompt(t("drawings.viewer.version_name_prompt"));
    if (!title?.trim()) return;

    try {
      setCreatingVersion(true);
      const response = await axios.post(`/drawings/${id}/drawing_versions`, {
        drawing_version: { title: title.trim() },
      });
      await loadVersions();
      await switchVersion(response.data.id);
      toast.success(t("drawings.viewer.version_created"));
    } catch (err) {
      console.error("Error creating drawing version:", err);
      toast.error(t("drawings.create_error"));
    } finally {
      setCreatingVersion(false);
    }
  };

  const measureLineFromModule = (mod) => {
    if (!mod || mod.module_type !== "measure") return null;
    const line = {
      x1: parseFloat(mod.x_position) || 0,
      y1: parseFloat(mod.y_position) || 0,
      x2: parseFloat(mod.x2_position) || 0,
      y2: parseFloat(mod.y2_position) || 0,
    };
    if (dist(line.x1, line.y1, line.x2, line.y2) < 8) return null;
    return line;
  };

  const handleSaveMeasureScale = async (nextScale) => {
    try {
      setSaving(true);
      await axios.put(`/drawings/${id}`, {
        drawing: { measure_scale_mm_per_unit: nextScale },
      });
      setMeasureScale(nextScale);
      setDrawing((current) =>
        current ? { ...current, measure_scale_mm_per_unit: nextScale } : current
      );
      setCalibrateOpen(false);
      setCalibrateLine(null);
      toast.success(t("drawings.viewer.calibrate_saved"));
    } catch (err) {
      console.error("Error saving measure scale:", err);
      toast.error(t("drawings.update_error"));
    } finally {
      setSaving(false);
    }
  };

  const startCalibrationLine = () => {
    setTool("measure");

    // Prefer selected measure, else latest measure on the sheet — so calibrate
    // works after the user already drew with the measure tool.
    const fromSelected = measureLineFromModule(selectedModule);
    if (fromSelected) {
      setCalibrateLine(fromSelected);
      setCalibrateOpen(true);
      toast(t("drawings.viewer.calibrate_using_existing"));
      return;
    }

    const measures = (modules || []).filter((m) => m.module_type === "measure");
    const latest = measures[measures.length - 1];
    const fromLatest = measureLineFromModule(latest);
    if (fromLatest) {
      setCalibrateLine(fromLatest);
      setCalibrateOpen(true);
      toast(t("drawings.viewer.calibrate_using_existing"));
      return;
    }

    // No measure yet: enter calibrate mode with a non-blocking banner so the
    // user can draw a reference line on the canvas (full modal would block it).
    setCalibrateLine(null);
    setCalibrateOpen(true);
    toast(t("drawings.viewer.calibrate_draw_line"));
  };

  const handlePinDeviation = (deviationId) => {
    pendingObservationRef.current = null;
    setPendingObservation(null);
    setPlacementGhost(null);
    setPinDeviationId(deviationId);
    setPanelTab("deviations");
    toast(t("drawings.viewer.pin_deviation_hint"));
  };

  const persistPendingPins = useCallback(
    (next) => {
      pendingPinsRef.current = next;
      savePendingPins(drawing?.id, next, activeVersionIdRef.current);
    },
    [drawing?.id]
  );

  const setDocumentPinWantSketch = useCallback((wantSketch) => {
    const next = Boolean(wantSketch);
    if (pendingObservationRef.current?.kind === "document_pin") {
      pendingObservationRef.current = {
        ...pendingObservationRef.current,
        wantSketch: next,
      };
      setPendingObservation((prev) =>
        prev?.kind === "document_pin" ? { ...prev, wantSketch: next } : prev
      );
    }
    updateDocumentPlacementSession({ wantSketch: next });
  }, []);

  const refreshModulesAfterDocumentUpload = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/drawings/${id}`, {
        params: { version_id: activeVersionId || "live" },
      });
      const serverModules = normalizeModules(response.data.drawing_modules || []);
      setModules((prev) => {
        const keepLocal = (prev || []).filter(
          (mod) =>
            mod._localPending &&
            mod._pendingPayload?.kind !== "document_pin" &&
            (mod._syncStatus === "pending" || mod._syncStatus === "error")
        );
        return [...serverModules, ...keepLocal];
      });
      // Drop persisted document_pin placeholders once server pins are loaded.
      persistPendingPins(
        (pendingPinsRef.current || []).filter(
          (pin) => pin.payload?.kind !== "document_pin"
        )
      );
    } catch (err) {
      console.error("Error refreshing drawing modules after document upload:", err);
    }
  }, [activeVersionId, id, persistPendingPins]);

  const cancelObservationPlacement = useCallback(() => {
    const pending = pendingObservationRef.current;
    pendingObservationRef.current = null;
    setPendingObservation(null);
    setPlacementGhost(null);
    setSketchSession(null);
    setRelocatingModuleId(null);
    retryExistingLocalIdRef.current = null;
    if (pending?.kind === "document_pin") {
      cancelDocumentPlacementSession();
      const safeReturn = sanitizeReturnPath(pending.returnPath);
      if (safeReturn) {
        navigate(safeReturn);
      }
    }
  }, [navigate]);

  const cancelIfcAlignment = useCallback(() => {
    setIfcAlignmentActive(false);
    setIfcAlignmentPoints([]);
    setIfcAlignmentDialogOpen(false);
  }, []);

  const startObservationPlacement = useCallback(
    (payload) => {
      setAddDialogOpen(false);
      pendingObservationRef.current = payload;
      setPendingObservation(payload);
      setPlacementGhost(null);
      setPinDeviationId(null);
      setRelocatingModuleId(null);
      setTool("select");
      if (typeof payload.page === "number" && payload.page !== page) {
        setPage(payload.page);
        setSelectedModule(null);
      }
      if (payload.kind === "document_pin") {
        toast(t("drawings.observation.place_hint"));
      }
    },
    [page, t]
  );

  const startDocumentPinPlacementFromSession = useCallback(
    (session) => {
      if (!session?.drawingId) return;
      startObservationPlacement({
        kind: "document_pin",
        title:
          session.title ||
          session.files?.[session.fileIndex]?.title ||
          t("documentUpload.preview.pickDrawingPosition"),
        wantSketch: Boolean(session.wantSketch),
        page,
        color: "#2ea66b",
        returnPath: session.returnPath || null,
        sessionId: session.id,
      });
    },
    [page, startObservationPlacement, t]
  );

  // After checklist/deviation document upload completes, pull server pins onto the open canvas.
  useEffect(() => {
    if (!drawing?.id) return undefined;

    const onDocumentsUploaded = (event) => {
      const detail = event?.detail || {};
      if (detail.module !== "checklist_item" && detail.module !== "deviation") {
        return;
      }
      const docs = Array.isArray(detail.documents) ? detail.documents : [];
      const touchesThisDrawing = docs.some(
        (doc) =>
          doc?.drawing_id != null &&
          String(doc.drawing_id) === String(drawing.id) &&
          doc.drawing_position_x != null &&
          doc.drawing_position_y != null
      );
      if (!touchesThisDrawing) return;
      void refreshModulesAfterDocumentUpload();
    };

    window.addEventListener("promang:documents-uploaded", onDocumentsUploaded);
    return () => {
      window.removeEventListener("promang:documents-uploaded", onDocumentsUploaded);
    };
  }, [drawing?.id, refreshModulesAfterDocumentUpload]);

  const upsertOptimisticPin = useCallback(
    (localPin) => {
      setModules((prev) => {
        const without = prev.filter((m) => m.id !== localPin.id);
        return [...without, localPin];
      });
      setSelectedModule(localPin);
      const meta = parseModuleMeta(localPin);
      const next = [
        ...pendingPinsRef.current.filter((p) => p.id !== localPin.id),
        {
          id: localPin.id,
          kind: meta.kind || localPin.module_type,
          module_type: localPin.module_type,
          x: localPin.x_position,
          y: localPin.y_position,
          width: localPin.width,
          height: localPin.height,
          color: localPin.color,
          path_data: localPin.path_data,
          payload: localPin._pendingPayload || null,
          syncStatus: localPin._syncStatus || "pending",
        },
      ];
      persistPendingPins(next);
    },
    [persistPendingPins]
  );

  const finishDocumentPinPlacement = useCallback(
    (payload, point, snapshotBlob = null) => {
      const pinPage = typeof payload?.page === "number" ? payload.page : page;

      completeDocumentPlacementSession({
        x: point.x,
        y: point.y,
        rotation: 0,
        page: pinPage,
        versionId: activeVersionId || null,
        snapshotBlob: snapshotBlob || null,
      });
      toast.success(t("documentUpload.preview.positionPlacedOnDrawing"));
      const safeReturn = sanitizeReturnPath(payload?.returnPath);
      if (safeReturn) {
        navigate(safeReturn);
      }
    },
    [activeVersionId, navigate, page, t]
  );

  const markLocalPinStatus = useCallback(
    (localId, status, serverModule = null) => {
      if (serverModule) {
        setModules((prev) =>
          prev.map((m) => (m.id === localId ? { ...serverModule } : m))
        );
        setSelectedModule(serverModule);
        persistPendingPins(pendingPinsRef.current.filter((p) => p.id !== localId));
        return;
      }
      setModules((prev) =>
        prev.map((m) =>
          m.id === localId
            ? { ...m, _syncStatus: status, _localPending: status !== "synced" }
            : m
        )
      );
      persistPendingPins(
        pendingPinsRef.current.map((p) =>
          p.id === localId ? { ...p, syncStatus: status } : p
        )
      );
    },
    [persistPendingPins]
  );

  // Persist partial payload data for a pending local pin (e.g. created IDs to enable idempotency)
  const stampPendingPinPayload = useCallback(
    (localId, patch) => {
      // Update persisted storage
      const nextPersisted = pendingPinsRef.current.map((p) =>
        p.id === localId ? { ...p, payload: { ...(p.payload || {}), ...patch } } : p
      );
      persistPendingPins(nextPersisted);
      // Update in-memory module mirror so future retries reuse data immediately
      setModules((prev) =>
        prev.map((m) =>
          m.id === localId
            ? { ...m, _pendingPayload: { ...(m._pendingPayload || {}), ...patch } }
            : m
        )
      );
    },
    [persistPendingPins]
  );

  const commitObservationPlacement = async (payload, point, options = {}) => {
    if (!drawing) return;
    const {
      sketchBlob = null,
      snapshotBlob = null,
      localId: existingLocalId = null,
    } = options;
    const pinX = point.x - OBSERVATION_PIN.width / 2;
    const pinY = point.y - OBSERVATION_PIN.height;
    const localId = existingLocalId || makeLocalPinId();

    const optimisticBase = {
      id: localId,
      _localPending: true,
      _syncStatus: "pending",
      _pendingPayload: {
        ...payload,
        photoFile: undefined,
        _hasPhotoFile: !!payload.photoFile,
      },
      x_position: pinX,
      y_position: pinY,
      width: OBSERVATION_PIN.width,
      height: OBSERVATION_PIN.height,
      color: payload.color,
    };

    try {
      if (payload.kind === "checklist") {
        if (!drawing.project?.id) {
          toast.error(t("drawings.observation.checklist_project_required"));
          return;
        }

        upsertOptimisticPin({
          ...optimisticBase,
          module_type: "checklist",
          path_data: stringifyModuleMeta({
            page: payload.page,
            kind: "checklist",
            title: payload.title,
          }),
        });

        let checklistId = payload.checklist_id;
        let checklistTitle = payload.title;

        // Create checklist only if requested and not already created/reused
        if (payload.createFromType && !checklistId) {
          const createPayload = {
            project_id: drawing.project.id,
            checklist_type_id: payload.checklist_type_id,
            title: payload.title,
            date: new Date().toISOString().split("T")[0],
            status: "open",
          };
          if (payload.house) createPayload.house = payload.house;
          if (payload.apartment) createPayload.apartment = payload.apartment;
          if (payload.selected_item_ids?.length) {
            createPayload.selected_item_ids = payload.selected_item_ids;
          }
          const result = await createChecklist(createPayload);

          if (
            result?.isAxiosError ||
            result?.response?.status >= 400 ||
            (Array.isArray(result?.errors) && result.errors.length)
          ) {
            markLocalPinStatus(localId, "error");
            toast.error(t("drawings.observation.create_error"));
            return;
          }

          const checklist = result?.data?.id ? result.data : result;
          checklistId = checklist?.id;
          checklistTitle =
            checklist?.displayTitle || checklist?.title || payload.title;
          if (!checklistId) {
            markLocalPinStatus(localId, "error");
            toast.error(t("drawings.observation.create_error"));
            return;
          }
          // Persist created ID to make retries idempotent
          stampPendingPinPayload(localId, { checklist_id: checklistId, createFromType: false });
          await loadRelatedData(drawing.project.id);
        }

        if (!checklistId) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }

        const created = await createModule(
          "checklist",
          pinX,
          pinY,
          OBSERVATION_PIN.width,
          OBSERVATION_PIN.height,
          {
            checklist_id: checklistId,
            color: payload.color || CHECKLIST_PIN_COLOR,
            path_data: stringifyModuleMeta({
              page: payload.page,
              kind: "checklist",
              checklist_id: checklistId,
              title: checklistTitle,
            }),
            silent: true,
            returnCreated: true,
          }
        );
        if (!created) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }
        markLocalPinStatus(localId, "synced", created);
        toast.success(t("drawings.observation.checklist_placed"));
        return;
      }

      if (payload.kind === "deviation") {
        if (!drawing.project?.id) {
          toast.error(t("drawings.observation.project_required"));
          return;
        }

        const marker = deviationMarkerPlacement(
          payload.module_type || DEFAULT_DEVIATION_MARKER_TYPE,
          point
        );
        upsertOptimisticPin({
          ...optimisticBase,
          module_type: marker.type,
          x_position: marker.x,
          y_position: marker.y,
          width: marker.width,
          height: marker.height,
          filled: false,
          path_data: stringifyModuleMeta({
            page: payload.page,
            kind: "deviation",
            title: payload.title,
          }),
        });

        // Reuse an already created deviation if available (idempotent retry)
        let deviationId = payload.deviation_id;
        let deviationObj = null;
        if (!deviationId) {
          // Persist on Deviation columns AND as EXPLANATION / CONSEQUENCE items —
          // the main avvik view reads items (latest_explanation_item / consequence_items),
          // not only the top-level description/consequence strings.
          const explanationText = payload.description?.trim() || "";
          const consequenceText = payload.consequence?.trim() || "";
          const consequenceType = payload.consequence_type
            ? Number(payload.consequence_type)
            : null;
          const items = [];
          if (explanationText) {
            items.push({
              title: "EXPLANATION",
              description: explanationText,
            });
          }
          if (consequenceText || consequenceType) {
            items.push({
              title: "CONSEQUENCE",
              description: consequenceText || explanationText || payload.title,
              ...(consequenceType ? { consequence: consequenceType } : {}),
            });
          }
          const placeHint =
            drawing.house ||
            drawing?.drawing_floor?.drawing_house?.name ||
            drawing.title ||
            null;
          const result = await createDeviation({
            title: payload.title,
            description: explanationText || null,
            consequence: consequenceText || null,
            place: placeHint,
            severity: payload.severityApiValue,
            project_id: drawing.project.id,
            status: 1,
            ...(items.length ? { items } : {}),
          });

          if (
            result?.isAxiosError ||
            result?.response?.status >= 400 ||
            (Array.isArray(result?.errors) && result.errors.length)
          ) {
            markLocalPinStatus(localId, "error");
            toast.error(t("drawings.observation.create_error"));
            return;
          }

          deviationObj = result?.data?.id ? result.data : result;
          deviationId = deviationObj?.id;
          if (!deviationId) {
            markLocalPinStatus(localId, "error");
            toast.error(t("drawings.observation.create_error"));
            return;
          }
          // Persist created ID to make retries idempotent
          stampPendingPinPayload(localId, { deviation_id: deviationId });
        }

        // Sketch is the marked image. snapshotBlob is the raw region crop.
        // Both are stored on the deviation, not only in the drawing-module meta.
        let deviationSketchDocumentId = null;
        let deviationSnapshotDocumentId = null;
        const uploadModule = drawing.project?.id ? "project" : "drawing";
        const uploadModuleParam = drawing.project?.id || drawing.id;
        const uploadCapture = async (blob, fileName) => {
          const file = new File([blob], fileName, { type: "image/jpeg" });
          const uploaded = await uploadDocumentFiles([
            {
              file,
              title: `${drawing.title} – ${t("drawings.observation.snapshot_title")} (${t("drawings.observation.page")} ${payload.page + 1})`,
              module: uploadModule,
              moduleParam: uploadModuleParam,
              skip_drawing_pin: true,
            },
          ]);
          return uploaded[0]?.id || null;
        };
        try {
          if (sketchBlob) {
            deviationSketchDocumentId = await uploadCapture(
              sketchBlob,
              `deviation-sketch-page-${payload.page + 1}.jpg`
            );
          }
          if (snapshotBlob && snapshotBlob !== sketchBlob) {
            deviationSnapshotDocumentId = await uploadCapture(
              snapshotBlob,
              `drawing-snapshot-page-${payload.page + 1}.jpg`
            );
          } else if (!sketchBlob && snapshotBlob) {
            deviationSnapshotDocumentId = await uploadCapture(
              snapshotBlob,
              `drawing-snapshot-page-${payload.page + 1}.jpg`
            );
          }
        } catch (e) {
          deviationSketchDocumentId = deviationSketchDocumentId || null;
          deviationSnapshotDocumentId = deviationSnapshotDocumentId || null;
        }

        const imageDocumentId = deviationSketchDocumentId || deviationSnapshotDocumentId;
        const snapshotDocumentId =
          deviationSnapshotDocumentId &&
          deviationSnapshotDocumentId !== imageDocumentId
            ? deviationSnapshotDocumentId
            : null;
        if (deviationId && imageDocumentId) {
          try {
            await attachDeviationDrawingCapture(deviationId, {
              image_document_id: imageDocumentId,
              drawing_snapshot_document_id: snapshotDocumentId,
            });
          } catch (attachError) {
            markLocalPinStatus(localId, "error");
            toast.error(t("drawings.observation.capture_bind_error"));
            return;
          }
        }

        const created = await createModule(
          marker.type,
          marker.x,
          marker.y,
          marker.width,
          marker.height,
          {
            deviation_id: deviationId,
            color: payload.color,
            filled: false,
            path_data: stringifyModuleMeta({
              page: payload.page,
              kind: "deviation",
              category: payload.category,
              severity: payload.severity,
              title: payload.title,
              description: payload.description?.trim() || null,
              consequence: payload.consequence?.trim() || null,
              consequence_type: payload.consequence_type || null,
              marker_type: marker.type,
              ...(imageDocumentId ? { snapshot_document_id: snapshotDocumentId || imageDocumentId } : {}),
            }),
            silent: true,
            returnCreated: true,
          }
        );
        if (!created) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }

        // If we just created a deviation, add it to local list for quick access
        if (deviationObj) {
          setDeviations((current) => [
            {
              id: deviationObj.id,
              number: deviationObj.number,
              displayTitle: deviationObj.displayTitle || deviationObj.title,
              title: deviationObj.title,
              status: deviationObj.status,
            },
            ...current,
          ]);
        }
        markLocalPinStatus(localId, "synced", created);
        toast.success(t("drawings.observation.deviation_placed"));
        return;
      }

      if (payload.kind === "photo") {
        upsertOptimisticPin({
          ...optimisticBase,
          module_type: "photo",
          path_data: stringifyModuleMeta({
            page: payload.page,
            kind: "photo",
            title: payload.title,
            caption: payload.caption,
            checklist_id: payload.checklist_id || null,
            checklist_title: payload.checklist_title || null,
          }),
        });

        if (!payload.photoFile) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }

        const linkedChecklistId = payload.checklist_id || null;
        const uploadModule = linkedChecklistId
          ? "checklist"
          : drawing.project?.id
            ? "project"
            : "drawing";
        const uploadModuleParam = linkedChecklistId
          ? linkedChecklistId
          : drawing.project?.id || drawing.id;
        const snapshotModule = drawing.project?.id ? "project" : "drawing";
        const snapshotModuleParam = drawing.project?.id || drawing.id;
        let snapshotBlob =
          sketchBlob ||
          (await captureDrawingRegionSnapshot(
            sheetRef.current,
            point,
            pdfBaseSize
          ));

        const uploaded = await uploadDocumentFiles([
          {
            file: payload.photoFile,
            title: payload.title,
            module: uploadModule,
            moduleParam: uploadModuleParam,
            drawing_id: drawing.id,
            drawing_position_x: point.x,
            drawing_position_y: point.y,
            drawing_page: payload.page,
            drawing_version_id: activeVersionId || null,
          },
        ]);

        const document = uploaded[0];
        if (!document?.id) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }

        let snapshotDocumentId = null;
        if (snapshotBlob) {
          const snapshotFile = new File(
            [snapshotBlob],
            `drawing-snapshot-page-${payload.page + 1}.jpg`,
            { type: "image/jpeg" }
          );
          const snapshotUpload = await uploadDocumentFiles([
            {
              file: snapshotFile,
              title: `${drawing.title} – ${t("drawings.observation.snapshot_title")} (${t("drawings.observation.page")} ${payload.page + 1})`,
              module: snapshotModule,
              moduleParam: snapshotModuleParam,
              // Snapshot helper: no drawing_id (avoids pin + position_missing).
              skip_drawing_pin: true,
            },
          ]);
          snapshotDocumentId = snapshotUpload[0]?.id || null;
          if (snapshotDocumentId) {
            await axios.put(`/documents/${document.id}`, {
              document: {
                drawing_snapshot_document_id: snapshotDocumentId,
                include_drawing_snapshot_in_print: true,
              },
            });
          }
        }

        const photoMeta = {
          page: payload.page,
          kind: "photo",
          document_id: document.id,
          snapshot_document_id: snapshotDocumentId,
          title: payload.title,
          caption: payload.caption,
          include_snapshot_in_print: true,
          checklist_id: linkedChecklistId,
          checklist_title: payload.checklist_title || null,
        };

        const created = await createModule(
          "photo",
          pinX,
          pinY,
          OBSERVATION_PIN.width,
          OBSERVATION_PIN.height,
          {
            color: payload.color,
            path_data: stringifyModuleMeta(photoMeta),
            silent: true,
            returnCreated: true,
          }
        );
        if (!created) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }
        markLocalPinStatus(localId, "synced", created);
        toast.success(t("drawings.observation.photo_placed"));
        return;
      }

      if (payload.kind === "note") {
        upsertOptimisticPin({
          ...optimisticBase,
          module_type: "note",
          path_data: stringifyModuleMeta({
            page: payload.page,
            kind: "note",
            title: payload.title,
          }),
        });
        const created = await createModule(
          "note",
          pinX,
          pinY,
          OBSERVATION_PIN.width,
          OBSERVATION_PIN.height,
          {
            color: payload.color,
            path_data: stringifyModuleMeta({
              page: payload.page,
              kind: "note",
              title: payload.title,
            }),
            silent: true,
            returnCreated: true,
          }
        );
        if (!created) {
          markLocalPinStatus(localId, "error");
          toast.error(t("drawings.observation.create_error"));
          return;
        }
        markLocalPinStatus(localId, "synced", created);
        toast.success(t("drawings.observation.note_placed"));
      }
    } catch (err) {
      console.error("Error placing observation:", err);
      markLocalPinStatus(localId, "error");
      toast.error(t("drawings.observation.create_error"));
    }
  };

  const placeObservationAt = async (clientX, clientY) => {
    // Allow placing even while other pins sync in the background.
    if (!pendingObservation || !drawing) return;

    const payload = pendingObservation;
    const point = toSheet(clientX, clientY);

    pendingObservationRef.current = null;
    setPendingObservation(null);
    setPlacementGhost(null);

    // Document pins always capture a region snapshot (burn-in sketch is optional).
    if (payload.kind === "document_pin") {
      const previewBlob = await captureDrawingRegionSnapshot(
        sheetRef.current,
        point,
        pdfBaseSize
      );
      if (payload.wantSketch) {
        const previewUrl = previewBlob ? URL.createObjectURL(previewBlob) : null;
        setSketchSession({
          payload,
          point,
          previewUrl,
          previewBlob,
        });
        return;
      }
      finishDocumentPinPlacement(payload, point, previewBlob || null);
      return;
    }

    if (
      payload.wantSketch &&
      (payload.kind === "photo" || payload.kind === "deviation")
    ) {
      const previewBlob = await captureDrawingRegionSnapshot(
        sheetRef.current,
        point,
        pdfBaseSize
      );
      const previewUrl = previewBlob ? URL.createObjectURL(previewBlob) : null;
      setSketchSession({
        payload,
        point,
        previewUrl,
        previewBlob,
      });
      return;
    }

    if (payload.kind === "deviation") {
      const previewBlob = await captureDrawingRegionSnapshot(
        sheetRef.current,
        point,
        pdfBaseSize
      );
      const existingLocalId = retryExistingLocalIdRef.current;
      await commitObservationPlacement(payload, point, {
        snapshotBlob: previewBlob || null,
        ...(existingLocalId ? { localId: existingLocalId } : {}),
      });
      retryExistingLocalIdRef.current = null;
      return;
    }

    const existingLocalId = retryExistingLocalIdRef.current;
    await commitObservationPlacement(
      payload,
      point,
      existingLocalId ? { localId: existingLocalId } : {}
    );
    retryExistingLocalIdRef.current = null;
  };

  const finishSketchSession = async ({ blob } = {}) => {
    if (!sketchSession) return;
    const { payload, point, previewUrl, previewBlob } = sketchSession;
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setSketchSession(null);

    if (payload.kind === "document_pin") {
      finishDocumentPinPlacement(payload, point, blob || previewBlob || null);
      return;
    }

    const existingLocalId = retryExistingLocalIdRef.current;
    await commitObservationPlacement(payload, point, {
      sketchBlob:
        payload.kind === "deviation" ? blob || null : blob || previewBlob || null,
      snapshotBlob: payload.kind === "deviation" ? previewBlob || null : null,
      ...(existingLocalId ? { localId: existingLocalId } : {}),
    });
    retryExistingLocalIdRef.current = null;
  };

  const skipSketchSession = async () => {
    if (!sketchSession) return;
    const { payload, point, previewUrl, previewBlob } = sketchSession;
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setSketchSession(null);

    if (payload.kind === "document_pin") {
      // Skip markup → still keep the region snapshot captured before the sketch overlay.
      finishDocumentPinPlacement(payload, point, previewBlob || null);
      return;
    }

    const existingLocalId = retryExistingLocalIdRef.current;
    await commitObservationPlacement(payload, point, {
      sketchBlob: payload.kind === "deviation" ? null : previewBlob,
      snapshotBlob: payload.kind === "deviation" ? previewBlob || null : null,
      ...(existingLocalId ? { localId: existingLocalId } : {}),
    });
    retryExistingLocalIdRef.current = null;
  };

  const cancelSketchSession = () => {
    const session = sketchSession;
    if (session?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(session.previewUrl);
    }
    setSketchSession(null);
    // Restore observation placement so the user can continue placing
    // without losing their configured payload.
    if (session?.payload) {
      startObservationPlacement(session.payload);
    }
  };

  const loadVersions = useCallback(async () => {
    try {
      const response = await axios.get(`/drawings/${id}/drawing_versions`);
      setVersions(response.data?.data || []);
    } catch (err) {
      console.error("Error loading drawing versions:", err);
    }
  }, [id]);

  const loadRelatedData = useCallback(async (projectId) => {
    if (!projectId) {
      setDeviations([]);
      setChecklists([]);
      return;
    }

    try {
      setRelatedLoading(true);
      const [deviationRows, checklistRows] = await Promise.all([
        loadProjectDeviations(projectId),
        loadProjectChecklists(projectId),
      ]);
      setDeviations(deviationRows);
      setChecklists(checklistRows);
    } catch (err) {
      console.error("Error loading drawing related data:", err);
    } finally {
      setRelatedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrawing();
  }, [loadDrawing]);

  // Keep refs in sync for use inside stable callbacks.
  useEffect(() => {
    activeVersionIdRef.current = activeVersionId;
  }, [activeVersionId]);
  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  useEffect(() => {
    if (drawing?.project?.id) {
      loadRelatedData(drawing.project.id);
    }
  }, [drawing?.project?.id, loadRelatedData]);

  // Auto-resume any locally pending pins after loading the drawing/version.
  useEffect(() => {
    if (!drawing?.id) return;
    // Only resume once per drawing+version load
    const key = `${drawing.id}:${activeVersionId || "live"}`;
    if (autoResumeKeyRef.current === key) return;

    const pendingLocals = (modules || []).filter(
      (m) => m?._localPending && m?._syncStatus === "pending"
    );
    if (pendingLocals.length === 0) return;

    // Mark as handled only when there are pending locals to resume.
    autoResumeKeyRef.current = key;

    pendingLocals.forEach((m) => {
      const payload = m._pendingPayload || parseModuleMeta(m) || {};
      const kind = payload.kind || parseModuleMeta(m).kind;
      // Photos require a File; if missing, surface a retry by marking as error.
      if (kind === "photo" && !payload.photoFile) {
        markLocalPinStatus(m.id, "error");
        return;
      }
      // Compute the original placement point so commitObservationPlacement recreates identical coords.
      let point;
      if (kind === "deviation" || ["arrow", "circle", "rectangular"].includes(m.module_type)) {
        const w = m.width || 40;
        const h = m.height || 40;
        point = { x: (m.x_position || 0) + w / 2, y: (m.y_position || 0) + h / 2 };
      } else {
        point = {
          x: (m.x_position || 0) + OBSERVATION_PIN.width / 2,
          y: (m.y_position || 0) + OBSERVATION_PIN.height,
        };
      }
      commitObservationPlacement(payload, point, { localId: m.id }).catch(() => {
        // On any failure, expose manual retry.
        markLocalPinStatus(m.id, "error");
      });
    });
  }, [drawing?.id, activeVersionId, modules, markLocalPinStatus]);

  const handleSaveLocation = async () => {
    try {
      setSavingLocation(true);
      // Preflight: prevent moving onto a floor that already has the same kind.
      const targetFloorId = draftDrawingFloorId || null;
      if (targetFloorId) {
        const targetKind = normalizeDrawingKind(drawing?.drawing_kind);
        let drawingsToCheck = siblingDrawings || [];
        try {
          drawingsToCheck = await fetchSiblingDrawings(drawing?.project?.id, {
            drawingFloorId: targetFloorId,
          });
        } catch (refreshErr) {
          // Fall back to cached rows if refresh fails; server-side update still validates.
          console.error("Error refreshing sibling drawings before save:", refreshErr);
        }
        const conflict = drawingsToCheck.find((row) => {
          const rowFloorId = row?.drawing_floor?.id || row?.drawing_floor_id;
          const rowKind = normalizeDrawingKind(row?.drawing_kind);
          return (
            String(rowFloorId) === String(targetFloorId) &&
            rowKind === targetKind &&
            String(row?.id) !== String(drawing?.id)
          );
        });
        if (conflict) {
          toast.error(t("drawings.floor_kind_conflict"));
          return;
        }
      }
      const response = await axios.put(`/drawings/${id}`, {
        drawing: {
          drawing_floor_id: draftDrawingFloorId || null,
        },
      });
      const updated = response.data;
      setDrawing((current) =>
        current
          ? {
              ...current,
              house: updated.house,
              floor: updated.floor,
              drawing_floor: updated.drawing_floor,
            }
          : current
      );
      setDraftDrawingFloorId(updated.drawing_floor?.id || null);
      toast.success(t("drawings.viewer.location_saved"));
    } catch (err) {
      console.error("Error saving drawing location:", err);
      toast.error(t("drawings.update_error"));
    } finally {
      setSavingLocation(false);
    }
  };

  const locationDirty =
    (draftDrawingFloorId || null) !== (drawing?.drawing_floor?.id || null);

  useEffect(() => {
    // New PDF / page size: allow one auto-fit pass.
    autoFitEnabledRef.current = true;
    const timeout = setTimeout(fit, 60);
    const el = containerEl;
    let didInitialFit = false;
    let lastSize = { w: 0, h: 0 };

    const maybeAutoFit = () => {
      if (!el || !autoFitEnabledRef.current) return;
      const rect = el.getBoundingClientRect();
      if (rect.width < 50 || rect.height < 50) return;

      if (!didInitialFit) {
        didInitialFit = true;
        lastSize = { w: rect.width, h: rect.height };
        fit();
        return;
      }

      // Re-fit only on major layout changes (panel / orientation), never on
      // mobile browser chrome (address-bar) resize which would undo zoom.
      const dw = Math.abs(rect.width - lastSize.w);
      const dh = Math.abs(rect.height - lastSize.h);
      lastSize = { w: rect.width, h: rect.height };
      if (dw > 120 || dh > 160) {
        fit();
      }
    };

    const observer =
      el && "ResizeObserver" in window
        ? new ResizeObserver(() => maybeAutoFit())
        : null;

    if (observer && el) observer.observe(el);

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
    };
  }, [containerEl, fit, pdfBaseSize.width, pdfBaseSize.height]);

  useEffect(() => {
    const el = containerEl;
    if (!el) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
        zoomAbout(
          zoomRef.current * factor,
          event.clientX - rect.left,
          event.clientY - rect.top
        );
      } else {
        markViewUserAdjusted();
        applyPan({
          x: panRef.current.x - event.deltaX,
          y: panRef.current.y - event.deltaY,
        });
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyPan, containerEl, markViewUserAdjusted, zoomAbout]);

  // Pinch-to-zoom (touch). Browser page-zoom is suppressed via touch-action + preventDefault.
  // Listeners must re-bind when containerEl mounts (after loading shell).
  useEffect(() => {
    const el = containerEl;
    if (!el) return undefined;

    const touchDistance = (touches) => {
      const a = touches[0];
      const b = touches[1];
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    };

    const touchCenter = (touches) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    const endPinch = () => {
      pinchRef.current = null;
      pinchActiveRef.current = false;
    };

    const onTouchStart = (event) => {
      if (isViewerChromeTarget(event.target)) return;
      if (event.touches.length < 2) return;

      const rect = el.getBoundingClientRect();
      const center = touchCenter(event.touches);
      const dist = touchDistance(event.touches);
      if (dist < 8) return;

      gestureRef.current = null;
      setIsPanning(false);
      pinchActiveRef.current = true;
      pinchRef.current = {
        dist0: dist,
        zoom0: zoomRef.current,
        originX: (center.x - rect.left - panRef.current.x) / zoomRef.current,
        originY: (center.y - rect.top - panRef.current.y) / zoomRef.current,
      };
      if (event.cancelable) event.preventDefault();
    };

    const onTouchMove = (event) => {
      // Block browser page-zoom / overscroll while interacting with the canvas.
      if (event.touches.length >= 2 || pinchActiveRef.current) {
        if (event.cancelable) event.preventDefault();
      }

      const pinch = pinchRef.current;
      if (!pinch || event.touches.length < 2) return;

      const rect = el.getBoundingClientRect();
      const center = touchCenter(event.touches);
      const dist = touchDistance(event.touches);
      if (dist < 8 || pinch.dist0 < 8) return;

      const nextZoom = clamp(pinch.zoom0 * (dist / pinch.dist0), 0.15, 6);
      const cx = center.x - rect.left;
      const cy = center.y - rect.top;
      markViewUserAdjusted();
      applyZoom(nextZoom);
      applyPan({
        x: cx - pinch.originX * nextZoom,
        y: cy - pinch.originY * nextZoom,
      });
    };

    const onTouchEnd = (event) => {
      if (event.touches.length < 2) endPinch();
    };

    // Safari (iOS) legacy gesture events — prevent document/page zoom.
    const onGesture = (event) => {
      if (event.cancelable) event.preventDefault();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    el.addEventListener("gesturestart", onGesture, { passive: false });
    el.addEventListener("gesturechange", onGesture, { passive: false });
    el.addEventListener("gestureend", onGesture, { passive: false });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("gesturestart", onGesture);
      el.removeEventListener("gesturechange", onGesture);
      el.removeEventListener("gestureend", onGesture);
      endPinch();
    };
  }, [applyPan, applyZoom, containerEl, markViewUserAdjusted]);


  useEffect(() => {
    if (pdfBaseSize.width > 0 && pdfBaseSize.height > 0 && modules.length > 0) {
      setModules((prevModules) =>
        prevModules.map((module) => {
          const moduleWidth = module.width || 50;
          const moduleHeight = module.height || 50;
          const clamped = clampModulePosition(
            module.x_position || 0,
            module.y_position || 0,
            moduleWidth,
            moduleHeight,
            pdfBaseSize
          );

          if (
            clamped.x !== module.x_position ||
            clamped.y !== module.y_position
          ) {
            return { ...module, x_position: clamped.x, y_position: clamped.y };
          }

          return module;
        })
      );
    }
  }, [pdfBaseSize]);

  const moduleVersionParams = () =>
    activeVersionId ? { drawing_version_id: activeVersionId } : {};

  const createModule = async (type, x, y, width, height, extra = {}) => {
    const { silent, returnCreated, ...moduleExtra } = extra;
    const defaults = DEFAULT_MODULE_SIZE[type] || DEFAULT_MODULE_SIZE.circle;
    const moduleWidth = width ?? defaults.width;
    const moduleHeight = height ?? defaults.height;

    const clamped = clampModulePosition(
      x,
      y,
      moduleWidth,
      moduleHeight,
      pdfBaseSize
    );

    try {
      const response = await axios({
        method: "post",
        url: `/drawings/${id}/drawing_modules`,
        params: activeVersionId ? { version_id: activeVersionId } : {},
        data: {
          drawing_module: {
            module_type: type,
            x_position: clamped.x,
            y_position: clamped.y,
            width: moduleWidth,
            height: moduleHeight,
            color: markupColor,
            filled: false,
            ...moduleVersionParams(),
            ...moduleExtra,
          },
        },
      });

      const created = normalizeModules([response.data])[0];
      const enriched =
        moduleExtra.deviation_id != null
          ? {
              ...created,
              deviation:
                created.deviation ||
                deviations.find((item) => item.id === moduleExtra.deviation_id),
            }
          : moduleExtra.checklist_id != null
            ? {
                ...created,
                checklist_id: created.checklist_id ?? moduleExtra.checklist_id,
                checklist:
                  created.checklist ||
                  checklists.find((item) => item.id === moduleExtra.checklist_id),
              }
            : created;

      if (returnCreated) {
        return enriched;
      }

      setModules((prev) => [...prev, enriched]);
      if (tool === "select") {
        setSelectedModule(enriched);
      }
      if (!silent && (type !== "circle" || !moduleExtra.deviation_id)) {
        toast.success(`${t("drawings.module.add")} ${t(`drawings.module.${type}`)}`);
      }
      return enriched;
    } catch (err) {
      console.error("Error adding module:", err);
      if (!returnCreated) {
        toast.error(t("drawings.create_error"));
      }
      return null;
    }
  };

  const createPenModule = async (points) => {
    const normalized = normalizePenPath(points);
    if (!normalized) return;

    try {
      const response = await axios({
        method: "post",
        url: `/drawings/${id}/drawing_modules`,
        params: activeVersionId ? { version_id: activeVersionId } : {},
        data: {
          drawing_module: {
            module_type: "pen",
            x_position: normalized.x_position,
            y_position: normalized.y_position,
            width: strokeWidth,
            height: normalized.height,
            path_data: normalized.path_data,
            color: markupColor,
            filled: false,
            ...moduleVersionParams(),
          },
        },
      });

      const created = normalizeModules([response.data])[0];
      setModules((prev) => [...prev, created]);
    } catch (err) {
      console.error("Error adding pen mark:", err);
      toast.error(t("drawings.create_error"));
    }
  };

  const createMeasureModule = async (x1, y1, x2, y2) => {
    if (dist(x1, y1, x2, y2) < 8) return;

    try {
      const response = await axios({
        method: "post",
        url: `/drawings/${id}/drawing_modules`,
        params: activeVersionId ? { version_id: activeVersionId } : {},
        data: {
          drawing_module: {
            module_type: "measure",
            x_position: x1,
            y_position: y1,
            x2_position: x2,
            y2_position: y2, // required for vertical / diagonal reference lines
            width: strokeWidth,
            color: markupColor,
            filled: false,
            ...moduleVersionParams(),
          },
        },
      });

      const created = normalizeModules([response.data])[0];
      setModules((prev) => [...prev, created]);
    } catch (err) {
      console.error("Error adding measurement:", err);
      toast.error(t("drawings.create_error"));
    }
  };

  const handleModuleMove = (moduleId, newPosition) => {
    const module = modules.find((item) => item.id === moduleId);
    const moduleWidth = module?.width || 50;
    const moduleHeight = module?.height || 50;

    const clamped = clampModulePosition(
      newPosition.x,
      newPosition.y,
      moduleWidth,
      moduleHeight,
      pdfBaseSize
    );

    setModules((prev) =>
      prev.map((item) =>
        item.id === moduleId
          ? { ...item, x_position: clamped.x, y_position: clamped.y }
          : item
      )
    );

    setPendingChanges((prev) => ({
      ...prev,
      [moduleId]: { x_position: clamped.x, y_position: clamped.y },
    }));

    // If we were in relocate mode for this module, finish the relocation on drag end.
    if (relocatingModuleId && moduleId === relocatingModuleId) {
      toast.success(t("drawings.observation.relocate_saved"));
      setRelocatingModuleId(null);
    }
  };

  const handleSaveAllChanges = async () => {
    if (pendingCount === 0) {
      toast(t("drawings.no_changes"));
      return;
    }

    setSaving(true);
    const entries = Object.entries(pendingChanges);
    const remaining = { ...pendingChanges };
    let completed = 0;
    try {
      // Sequential so a mid-batch failure does not toast full success while
      // earlier modules already persisted (Promise.all cannot report partial).
      for (const [moduleId, changes] of entries) {
        await axios({
          method: "put",
          url: `/drawing_modules/${moduleId}`,
          data: { drawing_module: changes },
        });
        delete remaining[moduleId];
        completed += 1;
      }
      setPendingChanges({});
      toast.success(t("drawings.updated"));
    } catch (err) {
      console.error("Error saving changes:", err);
      setPendingChanges(remaining);
      if (completed > 0) {
        toast.error(
          t("drawings.update_partial", {
            done: completed,
            remaining: Object.keys(remaining).length,
          })
        );
      } else {
        toast.error(t("drawings.update_error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = useCallback(
    async (moduleId) => {
      if (!window.confirm(t("drawings.module.delete_confirm"))) {
        return;
      }

      try {
        // If this is a local/optimistic pin, delete it client-side only.
        const isLocalPending =
          (typeof moduleId === "string" && moduleId.startsWith("local-")) ||
          pendingPinsRef.current.some((p) => p.id === moduleId);
        if (isLocalPending) {
          setModules((prev) => prev.filter((item) => item.id !== moduleId));
          setSelectedModule((current) => (current?.id === moduleId ? null : current));
          setPendingChanges((prev) => {
            const next = { ...prev };
            delete next[moduleId];
            return next;
          });
          // Remove from persisted pending pins
          const nextPending = pendingPinsRef.current.filter((p) => p.id !== moduleId);
          persistPendingPins(nextPending);
          toast.success(t("drawings.module.delete"));
          return;
        }

        await axios({
          method: "delete",
          url: `/drawing_modules/${moduleId}`,
        });
        setModules((prev) => prev.filter((item) => item.id !== moduleId));
        setSelectedModule((current) =>
          current?.id === moduleId ? null : current
        );
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[moduleId];
          return next;
        });
        toast.success(t("drawings.module.delete"));
      } catch (err) {
        console.error("Error deleting module:", err);
        toast.error(t("drawings.delete_error"));
      }
    },
    [t, persistPendingPins]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;

      if (addDialogOpen) return;

      if (ifcAlignmentActive) {
        if (event.key === "Escape") {
          cancelIfcAlignment();
          event.preventDefault();
        }
        return;
      }

      if (pendingObservation) {
        if (event.key === "Escape") {
          cancelObservationPlacement();
          event.preventDefault();
        }
        return;
      }

      if (event.key === " ") {
        setSpaceDown(true);
        event.preventDefault();
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        if (canEdit && selectedModule) {
          handleDeleteModule(selectedModule.id);
          event.preventDefault();
        }
        return;
      }

      if (event.key === "0") {
        fit();
        return;
      }

      if (event.key === "Escape") {
        setDraftRect(null);
        setDraftPen(null);
        setDraftMeasure(null);
        setSelectedModule(null);
        setPinDeviationId(null);
        setCalibrateLine(null);
        cancelObservationPlacement();
        if (calibrateOpen) {
          setCalibrateOpen(false);
        } else {
          setTool("select");
        }
        return;
      }

      const mapped = TOOL_KEYMAP[event.key.toLowerCase()];
      if (mapped && (canEdit || VIEW_ONLY_TOOLS.has(mapped))) setTool(mapped);
    };

    const onKeyUp = (event) => {
      if (event.key === " ") setSpaceDown(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [selectedModule, fit, handleDeleteModule, calibrateOpen, addDialogOpen, pendingObservation, cancelObservationPlacement, ifcAlignmentActive, cancelIfcAlignment, canEdit]);

  const handleModuleConfig = (module) => {
    if (module.module_type !== "rectangular") return;

    const moduleWithPendingChanges = pendingChanges[module.id]
      ? { ...module, ...pendingChanges[module.id] }
      : module;
    setModuleConfigDialog(moduleWithPendingChanges);
  };

  const handleSaveModuleConfig = async () => {
    if (!moduleConfigDialog) return;

    try {
      setSaving(true);
      const pendingPosition = pendingChanges[moduleConfigDialog.id];
      const updateData = {
        color: moduleConfigDialog.color,
        filled: moduleConfigDialog.filled,
      };

      if (pendingPosition) {
        updateData.x_position = pendingPosition.x_position;
        updateData.y_position = pendingPosition.y_position;
      }

      const response = await axios({
        method: "put",
        url: `/drawing_modules/${moduleConfigDialog.id}`,
        data: { drawing_module: updateData },
      });

      setModules((prev) =>
        prev.map((item) =>
          item.id === moduleConfigDialog.id ? response.data : item
        )
      );

      if (pendingPosition) {
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[moduleConfigDialog.id];
          return next;
        });
      }

      setModuleConfigDialog(null);
      toast.success(t("drawings.updated"));
    } catch (err) {
      console.error("Error updating module:", err);
      toast.error(t("drawings.update_error"));
    } finally {
      setSaving(false);
    }
  };

  const startPan = (event) => {
    gestureRef.current = {
      mode: "pan",
      sx: event.clientX,
      sy: event.clientY,
      px: panRef.current.x,
      py: panRef.current.y,
    };
    setIsPanning(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerDown = (event) => {
    if (pinchActiveRef.current) return;

    // Zoom dock / markup chrome — never steal pointer capture from buttons.
    if (isViewerChromeTarget(event.target)) return;

    if (ifcContextMenu) setIfcContextMenu(null);

    if (ifcAlignmentActive) {
      if (event.button === 0 && !isModuleHitTarget(event.target)) {
        addIfcAlignmentPoint(event.clientX, event.clientY);
      }
      return;
    }

    if (relocatingModuleId) {
      if (!canEdit) {
        setRelocatingModuleId(null);
        return;
      }
      if (event.button === 0 && !isModuleHitTarget(event.target)) {
        const point = toSheet(event.clientX, event.clientY);
        const module = modules.find((item) => item.id === relocatingModuleId);
        if (module) {
          const width = module.width || OBSERVATION_PIN.width;
          const height = module.height || OBSERVATION_PIN.height;
          const kind = parseModuleMeta(module).kind;
          const nextPos =
            kind === "deviation" ||
            ["arrow", "circle", "rectangular"].includes(module.module_type)
              ? { x: point.x - width / 2, y: point.y - height / 2 }
              : {
                  x: point.x - width / 2,
                  y: point.y - height,
                };
          handleModuleMove(relocatingModuleId, nextPos);
          toast.success(t("drawings.observation.relocate_saved"));
        }
        setRelocatingModuleId(null);
      }
      return;
    }

    if (pendingObservation) {
      if (
        canEdit &&
        event.button === 0 &&
        !isModuleHitTarget(event.target)
      ) {
        placeObservationAt(event.clientX, event.clientY);
      }
      return;
    }

    if (isModuleHitTarget(event.target)) {
      return;
    }

    if (event.button === 1) {
      startPan(event);
      return;
    }

    const panMode = tool === "pan" || spaceDown;
    if (panMode) {
      startPan(event);
      return;
    }

    if (tool === "select") {
      gestureRef.current = {
        mode: "maybe-pan",
        sx: event.clientX,
        sy: event.clientY,
        px: panRef.current.x,
        py: panRef.current.y,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    // View-only: pan/select only — block markup, pin, and calibrate gestures.
    if (!canEdit) return;

    if (pinDeviationId) {
      const point = toSheet(event.clientX, event.clientY);
      const deviationId = pinDeviationId;
      setPinDeviationId(null);
      createModule("circle", point.x - 20, point.y - 20, 40, 40, {
        deviation_id: deviationId,
        color: "#e05252",
        filled: false,
      });
      return;
    }

    if (!isShapeTool && !calibrateOpen) return;

    const point = toSheet(event.clientX, event.clientY);
    event.currentTarget.setPointerCapture(event.pointerId);

    if (tool === "pen") {
      gestureRef.current = { mode: "draw-pen" };
      setDraftPen({
        color: markupColor,
        width: strokeWidth,
        points: [point],
      });
      return;
    }

    if (tool === "measure") {
      gestureRef.current = { mode: "draw-measure" };
      setDraftMeasure({
        x1: point.x,
        y1: point.y,
        x2: point.x,
        y2: point.y,
        color: markupColor,
        width: strokeWidth,
      });
      return;
    }

    if (tool === "arrow" || tool === "circle") {
      createModule(tool, point.x, point.y);
      return;
    }

    if (tool === "rectangular") {
      gestureRef.current = { mode: "draw-rect" };
      setDraftRect({
        x: point.x,
        y: point.y,
        w: 0,
        h: 0,
        color: markupColor,
      });
    }
  };

  const handlePointerMove = (event) => {
    if (pinchActiveRef.current) return;

    if (pendingObservation) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setPlacementGhost({
          left: event.clientX - rect.left,
          top: event.clientY - rect.top,
        });
      }
      return;
    }

    const gesture = gestureRef.current;
    if (!gesture) return;

    if (gesture.mode === "pan") {
      markViewUserAdjusted();
      applyPan({
        x: gesture.px + (event.clientX - gesture.sx),
        y: gesture.py + (event.clientY - gesture.sy),
      });
      return;
    }

    if (gesture.mode === "maybe-pan") {
      if (Math.abs(event.clientX - gesture.sx) + Math.abs(event.clientY - gesture.sy) > 4) {
        gesture.mode = "pan";
        gesture.moved = true;
        setIsPanning(true);
      } else {
        return;
      }
      markViewUserAdjusted();
      applyPan({
        x: gesture.px + (event.clientX - gesture.sx),
        y: gesture.py + (event.clientY - gesture.sy),
      });
      return;
    }

    if (gesture.mode === "draw-rect" && draftRect) {
      const point = toSheet(event.clientX, event.clientY);
      setDraftRect((current) =>
        current
          ? {
              ...current,
              w: point.x - current.x,
              h: point.y - current.y,
            }
          : current
      );
      return;
    }

    if (gesture.mode === "draw-pen") {
      const point = toSheet(event.clientX, event.clientY);
      setDraftPen((current) =>
        current
          ? { ...current, points: [...current.points, point] }
          : current
      );
      return;
    }

    if (gesture.mode === "draw-measure" && draftMeasure) {
      const point = toSheet(event.clientX, event.clientY);
      let x2 = point.x;
      let y2 = point.y;
      if (event.shiftKey) {
        const dx = x2 - draftMeasure.x1;
        const dy = y2 - draftMeasure.y1;
        if (Math.abs(dx) > Math.abs(dy)) {
          y2 = draftMeasure.y1;
        } else {
          x2 = draftMeasure.x1;
        }
      }
      setDraftMeasure((current) =>
        current ? { ...current, x2, y2 } : current
      );
    }
  };

  const handlePointerUp = (event) => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    setIsPanning(false);

    if (!gesture) return;

    if (gesture.mode === "draw-rect" && draftRect) {
      const nx = Math.min(draftRect.x, draftRect.x + draftRect.w);
      const ny = Math.min(draftRect.y, draftRect.y + draftRect.h);
      const width = Math.abs(draftRect.w);
      const height = Math.abs(draftRect.h);
      setDraftRect(null);

      if (canEdit && width > 6 && height > 6) {
        createModule("rectangular", nx, ny, width, height);
      }
      return;
    }

    if (gesture.mode === "draw-pen" && draftPen?.points?.length > 2) {
      const points = draftPen.points;
      setDraftPen(null);
      if (canEdit) createPenModule(points);
      return;
    }

    if (gesture.mode === "draw-pen") {
      setDraftPen(null);
      return;
    }

    if (gesture.mode === "draw-measure" && draftMeasure) {
      const { x1, y1, x2, y2 } = draftMeasure;
      setDraftMeasure(null);

      if (calibrateOpen) {
        if (canEdit && dist(x1, y1, x2, y2) >= 8) {
          setCalibrateLine({ x1, y1, x2, y2 });
        }
        return;
      }

      if (canEdit) createMeasureModule(x1, y1, x2, y2);
      return;
    }

    if (gesture.mode === "maybe-pan" && !gesture.moved) {
      setSelectedModule(null);
    }
  };

  const handlePdfLoadSuccess = ({ numPages: totalPages }) => {
    setPdfError(null);
    setNumPages(totalPages || 1);
  };

  const handlePdfLoadError = (error) => {
    console.error("PDF load error:", error);
    setPdfError(error.message || t("drawings.viewer.pdf_error"));
    toast.error(t("drawings.load_error"));
  };

  const handlePageLoadSuccess = (loadedPage) => {
    const viewport = loadedPage.getViewport({ scale: 1.0 });
    // react-pdf re-fires onLoadSuccess whenever the render `scale` changes (e.g. our
    // zoom-driven resolution bump), but the scale:1.0 viewport is always the same size —
    // skip the state update so that doesn't cause an extra re-render on every zoom settle.
    setPdfBaseSize((current) =>
      current && current.width === viewport.width && current.height === viewport.height
        ? current
        : { width: viewport.width, height: viewport.height }
    );
    // If a center-on request was queued for this page, run it now that the page is loaded.
    const pending = pendingCenterRef.current;
    if (pending && typeof pending.page === "number" && pending.page === page) {
      pendingCenterRef.current = null;
      requestAnimationFrame(() => centerOn(pending.x, pending.y));
    }
  };

  const handlePageChange = (nextPage) => {
    const clampedPage = clamp(nextPage, 0, numPages - 1);
    setPage(clampedPage);
    setSelectedModule(null);
    setDraftRect(null);
    setDraftPen(null);
    setDraftMeasure(null);
  };

  const handleSelectModule = (module) => {
    setSelectedModule(module);
    setPanelTab("modules");
  };

  const handleOpenModuleDetail = (module) => {
    setSelectedModule(module);
    // Checklist pins open fill-out directly (no intermediate detail step).
    if (observationKindFromModule(module) === "checklist") {
      const checklistId = checklistIdFromModule(module);
      if (checklistId != null) {
        setModuleDetailModule(null);
        window.dispatchEvent(
          new CustomEvent("promang:open-checklist-fill", {
            detail: { checklistId },
          })
        );
        return;
      }
    }
    setModuleDetailModule(module);
    setPanelTab("modules");
  };

  const handleToggleSnapshotPrint = async (documentId, includeInPrint) => {
    try {
      await axios.put(`/documents/${documentId}`, {
        document: { include_drawing_snapshot_in_print: includeInPrint },
      });
      toast.success(t("drawings.observation.snapshot_print_saved"));
    } catch (err) {
      console.error("Error updating snapshot print setting:", err);
      toast.error(t("drawings.update_error"));
    }
  };

  const handleCenterModule = (module) => {
    if (!module) return;
    const meta = parseModuleMeta(module);

    let centerX = (module.x_position || 0) + (module.width || 50) / 2;
    let centerY = (module.y_position || 0) + (module.height || 50) / 2;

    if (module.module_type === "measure") {
      centerX =
        ((parseFloat(module.x_position) || 0) + (parseFloat(module.x2_position) || 0)) / 2;
      centerY =
        ((parseFloat(module.y_position) || 0) + (parseFloat(module.y2_position) || 0)) / 2;
    }

    if (typeof meta.page === "number" && meta.page !== page) {
      // Defer centering until the target page has loaded.
      pendingCenterRef.current = { x: centerX, y: centerY, page: meta.page };
      setPage(meta.page);
      setSelectedModule(module);
      return;
    }

    centerOn(centerX, centerY);
  };

  const attachExistingPhotoToChecklist = useCallback(
    async ({ documentId, photoModule = null, checklistId }) => {
      if (documentId == null || checklistId == null) {
        toast.error(t("drawings.observation.create_error"));
        return false;
      }
      const checklist = (checklists || []).find(
        (item) => String(item.id) === String(checklistId)
      );
      const checklistTitle =
        checklist?.displayTitle || checklist?.title || null;
      try {
        // Rebind the photo only. The snapshot document stays on the project/drawing.
        await axios.put(`/documents/${documentId}`, {
          document: { checklist_id: Number(checklistId) },
        });

        const moduleToUpdate =
          photoModule ||
          (modules || []).find(
            (item) =>
              String(documentIdFromModule(item)) === String(documentId)
          );

        if (moduleToUpdate?.id && !moduleToUpdate._localPending) {
          const meta = parseModuleMeta(moduleToUpdate);
          const nextMeta = {
            ...meta,
            kind: meta.kind || "photo",
            document_id: meta.document_id || documentId,
            checklist_id: Number(checklistId),
            checklist_title: checklistTitle,
          };
          const response = await axios.put(
            `/drawing_modules/${moduleToUpdate.id}`,
            {
              drawing_module: {
                checklist_id: Number(checklistId),
                path_data: stringifyModuleMeta(nextMeta),
              },
            }
          );
          const updated = normalizeModules([response.data])[0];
          const merged = {
            ...moduleToUpdate,
            ...updated,
            checklist_id: Number(checklistId),
            path_data: stringifyModuleMeta(nextMeta),
            checklist: checklist || moduleToUpdate.checklist,
          };
          setModules((prev) =>
            prev.map((item) => (item.id === moduleToUpdate.id ? merged : item))
          );
          setSelectedModule((current) =>
            current?.id === moduleToUpdate.id ? merged : current
          );
          setModuleDetailModule((current) =>
            current?.id === moduleToUpdate.id ? merged : current
          );
        }

        toast.success(t("drawings.viewer.checklist_photo_linked"));
        return true;
      } catch (err) {
        console.error(
          "Error linking photo document to checklist:",
          err?.message || "request failed"
        );
        toast.error(t("drawings.observation.create_error"));
        return false;
      }
    },
    [checklists, modules, t]
  );

  const openAddDialog = useCallback((options = "photo") => {
    if (typeof options === "string") {
      setAddDialogKind(options || "photo");
      setAddDialogChecklistId(null);
      setAddDialogChecklistMode(null);
    } else {
      setAddDialogKind(options?.kind || "photo");
      setAddDialogChecklistId(options?.checklistId ?? null);
      setAddDialogChecklistMode(options?.checklistMode ?? null);
    }
    setAddDialogOpen(true);
  }, []);

  const startRelocateModule = useCallback((module) => {
    if (!module?.id) return;
    setRelocatingModuleId(module.id);
    setSelectedModule(module);
    pendingObservationRef.current = null;
    setPendingObservation(null);
    setTool("select");
    toast(t("drawings.observation.relocate_hint"));
  }, [t]);

  // Resume checklist/document pin placement after navigating to this drawing.
  useEffect(() => {
    if (!canEdit || !drawing?.id || loading) return;
    const session = getDocumentPlacementSession();
    if (!session || session.status !== "placing") return;
    if (String(session.drawingId) !== String(drawing.id)) return;
    if (documentPinSessionStartedRef.current === session.id) return;
    if (pendingObservationRef.current?.kind === "document_pin") {
      documentPinSessionStartedRef.current = session.id;
      return;
    }
    documentPinSessionStartedRef.current = session.id;
    startDocumentPinPlacementFromSession(session);
  }, [canEdit, drawing?.id, loading, startDocumentPinPlacementFromSession]);

  const isModuleDraggable = useCallback(
    (module) => {
      if (!module) return false;
      if (module._localPending) return false;
      if (relocatingModuleId && module.id === relocatingModuleId) return true;
      if (isLockedObservationPin(module)) return false;
      return true;
    },
    [relocatingModuleId]
  );

  const retryFailedPin = useCallback(
    (module) => {
      if (!module) return;
      const kind =
        module._pendingPayload?.kind ||
        parseModuleMeta(module).kind ||
        module.module_type ||
        "photo";
      // Keep the visible pin, but re-open the dialog so the user can re-submit
      // (photo File cannot be restored from localStorage).
      setModules((prev) =>
        prev.map((m) =>
          m.id === module.id ? { ...m, _syncStatus: "error", _localPending: true } : m
        )
      );
      // Reuse the same local id on successful retry to avoid duplicates.
      retryExistingLocalIdRef.current = module.id;
      if (kind === "photo") {
        toast.error(t("drawings.observation.retry_needs_photo"));
      } else {
        toast(t("drawings.observation.retry_save"));
      }
      if (kind === "checklist") {
        openAddDialog({
          kind: "checklist",
          checklistMode: module._pendingPayload?.createFromType === false ? "link" : "new",
          checklistId: module._pendingPayload?.checklist_id ?? null,
        });
      } else {
        openAddDialog(kind);
      }
    },
    [t, openAddDialog]
  );

  // If the add dialog closes without placing, clear any carried-over retry id.
  useEffect(() => {
    if (!addDialogOpen) {
      retryExistingLocalIdRef.current = null;
    }
  }, [addDialogOpen]);

  useEffect(() => {
    if (!drawing?.id) {
      setSiblingDrawings([]);
      return undefined;
    }

    let cancelled = false;
    setSiblingDrawingsLoading(true);
    fetchSiblingDrawings(drawing.project?.id, { shouldStop: () => cancelled })
      .then((rows) => {
        if (cancelled) return;
        setSiblingDrawings(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading sibling drawings:", err);
        setSiblingDrawings([]);
      })
      .finally(() => {
        if (!cancelled) setSiblingDrawingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [drawing?.id, drawing?.project?.id, fetchSiblingDrawings]);

  const goToEdit = () => navigate(`/dashboard/drawings/${id}/edit`);

  // ---------------------------------------------------------------------------
  // Promang IFC Viewer (company module `ifc_viewer`) — «Se 3D» / «Åpne sted i 3D»
  // ---------------------------------------------------------------------------

  const ifcModuleActive = useCompanyHasModule("ifc_viewer");
  const ifcRoleAccess = useUserHasAccess({
    roles: ["ifc_viewer", "ifc_viewer_edit", "admin"],
  });
  const ifcCanEditAccess = useUserHasAccess({
    roles: ["ifc_viewer_edit", "admin"],
  });
  const ifcEnabled = Boolean(ifcModuleActive && ifcRoleAccess);
  const ifcCanEdit = Boolean(ifcEnabled && ifcCanEditAccess);

  const drawingProjectId = drawing?.project?.id || null;
  const drawingHouseId = drawing?.drawing_floor?.drawing_house?.id || null;

  const buildIfcViewerHref = useCallback(
    (params) => {
      if (!drawingProjectId) return null;
      const search = new URLSearchParams();
      if (drawingHouseId) search.set("house", String(drawingHouseId));
      search.set("drawing", String(id));
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          search.set(key, String(value));
        }
      });
      return `/dashboard/projects/${drawingProjectId}/ifc-viewer?${search.toString()}`;
    },
    [drawingProjectId, drawingHouseId, id]
  );

  const openIfcViewerTab = useCallback(
    (params) => {
      const href = buildIfcViewerHref(params);
      if (!href) return;
      return window.open(href, "_blank");
    },
    [buildIfcViewerHref]
  );

  const handleOpen3d = useCallback(() => {
    if (!ifcEnabled) return;
    if (!drawingProjectId) {
      toast.error(t("drawings.viewer.see_3d_no_project"));
      return;
    }
    const href = buildIfcViewerHref();
    if (href) navigate(href);
  }, [ifcEnabled, drawingProjectId, buildIfcViewerHref, navigate, t]);

  const closeIfcContextMenu = useCallback(() => setIfcContextMenu(null), []);

  const handleCanvasContextMenu = useCallback(
    (event) => {
      if (!ifcEnabled || !drawingProjectId) return;
      if (pendingObservation || ifcAlignmentActive) return;
      event.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      const point = toSheet(event.clientX, event.clientY);
      setIfcContextMenu({
        left: rect ? event.clientX - rect.left : event.clientX,
        top: rect ? event.clientY - rect.top : event.clientY,
        pdfX: point.x,
        pdfY: point.y,
      });
    },
    [ifcEnabled, drawingProjectId, pendingObservation, ifcAlignmentActive, toSheet]
  );

  const handleOpenPlaceIn3d = useCallback(
    async (pdfX, pdfY) => {
      setIfcContextMenu(null);
      if (!ifcEnabled || !drawingProjectId) return;
      let initialWin = null;
      try {
        // Open a blank tab synchronously to preserve the user gesture and avoid popup blocking,
        // then navigate it once the focus payload is ready (prevents double session creation).
        initialWin = window.open("about:blank", "_blank");
        const result = await openDrawingIn3d(id, { x: pdfX, y: pdfY, page });
        const focus = result?.focus || null;
        if (!focus) {
          toast(t("drawings.viewer.open_place_in_3d_no_alignment"));
          // Do not proceed to open the IFC viewer without a focus; close the pre-opened tab.
          if (initialWin && !initialWin.closed) {
            try {
              initialWin.close();
            } catch (_) {
              /* ignore */
            }
          }
          return;
        }
        // Build final URL and navigate the pre-opened tab if available; otherwise open a new one.
        const search = new URLSearchParams();
        const houseParam = result?.drawing_house_id || drawingHouseId || "";
        if (houseParam) search.set("house", String(houseParam));
        search.set("drawing", String(id));
        if (focus?.x != null) search.set("focusX", String(focus.x));
        if (focus?.y != null) search.set("focusY", String(focus.y));
        search.set("focusZ", String(focus?.z ?? 0));
        if (focus?.storey_name) search.set("storey", String(focus.storey_name));
        const finalUrl = `/dashboard/projects/${drawingProjectId}/ifc-viewer?${search.toString()}`;
        if (initialWin && !initialWin.closed) {
          initialWin.location.href = finalUrl;
        } else {
          const win = openIfcViewerTab({
            house: houseParam,
            focusX: focus?.x,
            focusY: focus?.y,
            focusZ: focus?.z ?? 0,
            storey: focus?.storey_name || "",
          });
          if (!win) {
            // Popup likely blocked by the browser.
            toast.error(t("drawings.viewer.open_place_in_3d_error"));
          }
        }
      } catch (err) {
        // Close the pre-opened blank tab on failure.
        if (initialWin && !initialWin.closed) {
          try {
            initialWin.close();
          } catch (_) {
            /* ignore */
          }
        }
        toast.error(t("drawings.viewer.open_place_in_3d_error"));
      }
    },
    [ifcEnabled, drawingProjectId, id, drawingHouseId, openIfcViewerTab, page, t]
  );

  const startIfcAlignment = useCallback(() => {
    if (!ifcCanEdit || !drawingProjectId) return;
    setIfcContextMenu(null);
    setPendingObservation(null);
    setPlacementGhost(null);
    setPinDeviationId(null);
    setTool("select");
    setIfcAlignmentDialogOpen(false);
    setIfcAlignmentPoints([]);
    setIfcAlignmentActive(false);
    const search = new URLSearchParams();
    if (drawingHouseId) search.set("house", String(drawingHouseId));
    search.set("linkDrawing", String(id));
    window.location.assign(
      `/dashboard/projects/${drawingProjectId}/ifc-viewer?${search.toString()}`
    );
  }, [ifcCanEdit, drawingProjectId, drawingHouseId, id]);

  const addIfcAlignmentPoint = useCallback(
    (clientX, clientY) => {
      const point = toSheet(clientX, clientY);
      setIfcAlignmentPoints((current) => {
        const next = [...current, { x: point.x, y: point.y }];
        if (next.length >= 2) {
          setIfcAlignmentActive(false);
          setIfcAlignmentDialogOpen(true);
          return next.slice(0, 2);
        }
        toast(t("drawings.viewer.align_3d_pick_second"));
        return next;
      });
    },
    [toSheet, t]
  );

  const handleSaveIfcAlignment = useCallback(
    async (payload) => {
      if (!drawingProjectId) return;
      try {
        setSavingIfcAlignment(true);
        await createDrawingIfcAlignment(drawingProjectId, {
          drawing_id: id,
          page,
          ...payload,
        });
        toast.success(t("drawings.viewer.align_3d_saved"));
        cancelIfcAlignment();
      } catch (err) {
        console.error("Error saving drawing/IFC alignment:", err);
        toast.error(t("drawings.viewer.align_3d_error"));
      } finally {
        setSavingIfcAlignment(false);
      }
    },
    [drawingProjectId, id, page, drawingHouseId, cancelIfcAlignment, t]
  );

  const workspaceClass =
    pendingObservation || pinDeviationId || relocatingModuleId || ifcAlignmentActive
      ? "cursor-crosshair"
      : tool === "pan" || spaceDown
      ? isPanning
        ? "cursor-grabbing"
        : "cursor-grab"
      : isShapeTool || calibrateOpen
        ? "cursor-crosshair"
        : "cursor-default";

  return {
    t,
    canEdit,
    drawing,
    loading,
    pdfUrl,
    zoom,
    pan,
    pdfBaseSize,
    numPages,
    page,
    modules,
    visibleModules,
    tool,
    setTool: setEditTool,
    markupColor,
    setMarkupColor,
    selectedModule,
    setSelectedModule: handleSelectModule,
    moduleConfigDialog,
    setModuleConfigDialog,
    draftRect,
    draftPen,
    draftMeasure,
    strokeWidth,
    setStrokeWidth,
    saving,
    pdfError,
    pendingChanges,
    pendingCount,
    panelOpen,
    setPanelOpen,
    panelTab,
    setPanelTab,
    isShapeTool,
    workspaceClass,
    containerRef: setContainerRef,
    sheetRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleModuleMove: guardEdit(handleModuleMove),
    handleSaveAllChanges: guardEdit(handleSaveAllChanges),
    handleDeleteModule: guardEdit(handleDeleteModule),
    handleModuleConfig: guardEdit(handleModuleConfig),
    handleSaveModuleConfig: guardEdit(handleSaveModuleConfig),
    setZoomCentered,
    fit,
    handlePdfLoadSuccess,
    handlePdfLoadError,
    handlePageLoadSuccess,
    handlePageChange,
    handleCenterModule,
    goToEdit: guardEdit(goToEdit),
    measureScale,
    activeVersionId,
    activeVersion,
    versions,
    isVersionMode,
    deviations,
    checklists,
    relatedLoading,
    loadRelatedData,
    pinDeviationId,
    calibrateOpen,
    setCalibrateOpen,
    calibrateLine,
    setCalibrateLine,
    expandedChecklistId,
    setExpandedChecklistId,
    creatingVersion,
    switchVersion,
    handleCreateVersion: guardEdit(handleCreateVersion),
    handleSaveMeasureScale: guardEdit(handleSaveMeasureScale),
    startCalibrationLine: guardEdit(startCalibrationLine),
    handlePinDeviation: guardEdit(handlePinDeviation),
    loadVersions,
    draftDrawingFloorId,
    setDraftDrawingFloorId,
    locationDirty,
    savingLocation,
    handleSaveLocation: guardEdit(handleSaveLocation),
    addDialogOpen,
    setAddDialogOpen,
    addDialogKind,
    addDialogChecklistId,
    addDialogChecklistMode,
    openAddDialog: guardEdit(openAddDialog),
    attachExistingPhotoToChecklist: guardEdit(attachExistingPhotoToChecklist),
    openAddChecklist: guardEdit(() =>
      openAddDialog({ kind: "checklist", checklistMode: "new" })),
    openLinkChecklist: guardEdit((checklistId) =>
      openAddDialog({
        kind: "checklist",
        checklistMode: "link",
        checklistId,
      })),
    pendingObservation,
    placementGhost,
    placingObservation: !!pendingObservation,
    startObservationPlacement: guardEdit(startObservationPlacement),
    startDocumentPinPlacementFromSession: guardEdit(
      startDocumentPinPlacementFromSession
    ),
    setDocumentPinWantSketch,
    cancelObservationPlacement,
    sketchSession,
    finishSketchSession,
    skipSketchSession,
    cancelSketchSession,
    relocatingModuleId,
    startRelocateModule: guardEdit(startRelocateModule),
    isModuleDraggable: canEdit ? isModuleDraggable : () => false,
    retryFailedPin: guardEdit(retryFailedPin),
    moduleDetailModule,
    setModuleDetailModule,
    handleOpenModuleDetail,
    handleToggleSnapshotPrint: guardEdit(handleToggleSnapshotPrint),
    siblingDrawings,
    siblingDrawingsLoading,
    ifcEnabled,
    ifcModuleActive,
    ifcRoleAccess,
    ifcCanEdit,
    ifcContextMenu,
    closeIfcContextMenu,
    handleCanvasContextMenu,
    handleOpen3d,
    handleOpenPlaceIn3d,
    ifcAlignmentActive,
    ifcAlignmentPoints,
    ifcAlignmentDialogOpen,
    savingIfcAlignment,
    startIfcAlignment,
    cancelIfcAlignment,
    handleSaveIfcAlignment,
  };
};

